/**
 * Legislative History.
 *
 * **Call it Legislative History, or "a record". Never "Project Threads", "Threads", or
 * "Entities" — those names were explicitly rejected.** The underlying Atlas tables are still
 * named `threads` / `thread_members`, which is why this module exists: it is the translation
 * layer between the schema's vocabulary and the product's.
 *
 * ## How a record comes to exist
 *
 * Records are NOT enumerated from the corpus. A record exists because:
 *   (a) a user's Signal matched an item, and
 *   (b) matched items sharing the same spoken ordinance/resolution number — the pipeline's
 *       `num`, preserved verbatim per summarizer Rule 3 — within ONE town are grouped.
 *
 * Two or more meetings sharing a number becomes a record. This is what keeps volume sane
 * across thousands of towns: relevance comes from what the user already told the product they
 * care about, the same principle as Signal Feed. A user can also promote an item by hand with
 * "Track [number]", for the case where no Signal caught it.
 *
 * Dedupe is on `(town, num)`. Tracking an already-tracked number opens the existing record
 * rather than creating a second one.
 */
import type { BodyType } from '@/lib/types';

/**
 * Why an item belongs to a record. Maps to `thread_members.match_basis` in Atlas.
 *
 * The hard/soft distinction is load-bearing and must stay visible in the UI: a shared
 * ordinance number is a fact, a shared project name is an inference. Never present them
 * identically, and never hide the inferred ones behind a confidence score.
 */
export type MatchBasis = 'ordinance_num' | 'project_name' | 'parcel';

export function isHardMatch(basis: MatchBasis): boolean {
  return basis === 'ordinance_num';
}

export const MATCH_BASIS_LABEL: Record<MatchBasis, string> = {
  ordinance_num: 'Same ordinance number',
  project_name: 'Same project named',
  parcel: 'Same parcel referenced',
};

/** The lifecycle a numbered item moves through. Order matters — it drives the timeline. */
export const STAGES = [
  'First mention',
  'Application',
  'Planning board',
  'First reading',
  'Second reading',
  'Adopted',
] as const;
export type Stage = (typeof STAGES)[number];

export interface RecordItem {
  meetingId: string;
  /** The item's own date, mm/dd/yyyy. */
  date: string;
  /** Item type from the pipeline, e.g. "Ordinance", "First reading". */
  type: string | null;
  /** The spoken number, verbatim. Null for an item that joined on a soft signal. */
  num: string | null;
  name: string;
  summary: string;
  body: BodyType;
  bodyName: string;
  /**
   * `"Unanimous"`, `"Passed 3-2"`, or null. Non-null means voted on; null means discussed with
   * no vote taken. Read directly — never inferred from the item type or the summary.
   */
  vote: string | null;
  basis: MatchBasis;
  /** 0–1 from the resolution pass. Only meaningful for soft matches. */
  matchScore: number | null;
  /** What the soft match keyed on, e.g. "Riverside Solar LLC". */
  matchedOn: string | null;
}

export interface LegislativeRecord {
  id: string;
  /** The number this record is keyed on, or null where the record has none yet. */
  num: string | null;
  /** Canonical label, e.g. "Ordinance 2026-14 — Riverside Solar Array". */
  label: string;
  townName: string;
  townRegion: string;
  body: BodyType;
  stage: Stage;
  /** One line of plain English, e.g. "Held pending a stormwater plan". */
  status: string;
  meetingCount: number;
  firstSeen: string;
  lastSeen: string;
  /** Sort key: most recent activity. Never displayed. */
  lastActivity: number;
  /**
   * Which Signal produced this record, or null when the user tracked it by hand — surfaced as
   * "Added by you". Provenance is the point of the surface; a record with no stated origin is
   * a record the user cannot trust.
   */
  signalName: string | null;
  items: RecordItem[];
}

/** True when any member joined on something softer than a shared number. */
export function hasSoftMatch(record: LegislativeRecord): boolean {
  return record.items.some((item) => !isHardMatch(item.basis));
}

/** Items with no number of their own — shown as "No number yet", never hidden. */
export function unnumberedItems(record: LegislativeRecord): RecordItem[] {
  return record.items.filter((item) => item.num === null);
}

/**
 * The list is sorted by most recent activity, and that is the only sort.
 *
 * There is deliberately no user-facing sort switcher — a design decision to keep the surface
 * simple. Do not add one without revisiting that decision.
 */
export function sortByActivity(records: LegislativeRecord[]): LegislativeRecord[] {
  return [...records].sort((a, b) => b.lastActivity - a.lastActivity);
}
